import { useState } from 'react';
import { Check, ListFilter, LucideIcon } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from './atoms/Button';
import { DropdownMenu } from './organisms/DropdownMenu';
import { getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import type { GTMTrackingAttributes } from '../../utils/gtm/gtm-tracking';

export interface FilterOption<T extends string> {
  value: T;
  label: string;
  icon: LucideIcon;
}

export interface FilterProps<T extends string> {
  filters: FilterOption<T>[];
  selected: T;
  onChange: (value: T) => void;
  defaultValue?: T;
  ariaLabel?: string;
  className?: string;
  tooltipText?: string;
  dropdownPosition?: 'left' | 'right';
  /** Dense trigger for tight contexts (e.g. a table column header) that must not grow the row. */
  compact?: boolean;
  /** Optional GTM tracking for each filter option (receives filter value, returns attributes) */
  getFilterItemTracking?: (value: T) => GTMTrackingAttributes;
}

/**
 * Reusable Filter component — selection menu on DropdownMenu.
 */
export function Filter<T extends string>({
  filters,
  selected,
  onChange,
  defaultValue,
  ariaLabel = 'Filter options',
  className,
  tooltipText,
  dropdownPosition = 'right',
  compact = false,
  getFilterItemTracking,
}: FilterProps<T>) {
  const [open, setOpen] = useState(false);

  const selectedFilter = filters.find(f => f.value === selected) || filters[0];
  const isFiltered = defaultValue ? selected !== defaultValue : false;

  const handleFilterChange = (value: T) => {
    onChange(value);
    setOpen(false);
  };

  const tooltipLabel = tooltipText || (isFiltered ? `Filtered: ${selectedFilter.label}` : 'Filter options');
  const align = dropdownPosition === 'left' ? 'start' : 'end';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} className={className}>
      <span className="relative inline-flex">
        <DropdownMenu.Trigger asChild>
          <Button
            type="icon"
            intent="muted"
            size={compact ? 'sm' : 'md'}
            selected={isFiltered}
            aria-label={ariaLabel}
            label={ariaLabel}
            title={tooltipLabel}
            icon={<ListFilter className="stroke-2" />}
            className={cn(isFiltered && 'text-purple')}
          />
        </DropdownMenu.Trigger>
        {isFiltered && (
          <span
            className={cn(
              'pointer-events-none absolute z-[3] rounded-full bg-purple ring-1 ring-light',
              compact ? '-right-0.5 -top-0.5 h-1.5 w-1.5' : '-right-0 -top-1 h-2.5 w-2.5 border-2 border-light'
            )}
            aria-hidden
          />
        )}
      </span>
      <DropdownMenu.Content align={align}>
        {filters.map((filter) => {
          const FilterIcon = filter.icon;
          const isSelected = selected === filter.value;

          return (
            <DropdownMenu.Item
              key={filter.value}
              {...(getFilterItemTracking ? getGTMAttributes(getFilterItemTracking(filter.value)) : {})}
              onClick={() => handleFilterChange(filter.value)}
              className={cn(
                'flex items-center gap-3',
                isSelected && 'bg-purple-soft'
              )}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {isSelected && <Check className="w-4 h-4 text-dark-invirtus" />}
              </div>
              <FilterIcon
                className={cn(
                  'w-4 h-4 transition-colors',
                  isSelected ? 'text-dark-invirtus' : 'text-dark-invirtus-strong'
                )}
              />
              <span className="text-dark-invirtus-strong">{filter.label}</span>
            </DropdownMenu.Item>
          );
        })}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
